import { useInfiniteQuery } from '@tanstack/react-query';
import type { AxiosRequestConfig } from 'axios';

import { oprApi } from '~/shared/services/api';

export type GetFeatureFlagsAPIResponse = {
  id: string;
  name: string;
  description: string;
  status: boolean;
  createdAt: Date;
  updatedAt: Date;
}[];

export const FEATURE_FLAGS_QUERY_KEY = 'useGetFeatureFlags';

export function transformFlagResponseIntoFlags(
  flagsResponse: GetFeatureFlagsAPIResponse = []
) {
  const rawFlags: Record<string, boolean> = flagsResponse.reduce(
    (acc, curr) => {
      acc[curr.name] = curr.status;
      return acc;
    },
    {} as Record<string, boolean>
  );

  const flags = {
    home: {
      active: rawFlags?.PAGE_HOME_ENABLED || false,
      skippable: true,
    },
    oprProcess: {
      active: rawFlags?.PAGE_HOME_ENABLED || false,
      skippable: false,
    },
    dashboardPersonal: {
      active: rawFlags?.PAGE_DASHBOARD_PERSONAL_ENABLED || false,
      skippable: false,
    },
    dashboardManager: {
      active: rawFlags?.PAGE_DASHBOARD_MANAGER_ENABLED || false,
      skippable: false,
    },
    eligibilityCriteriaExceptions: {
      active: rawFlags?.PAGE_ELIGIBILITY_CRITERIA_EXCEPTIONS_ENABLED || false,
      skippable: true,
    },
    eligibilityCriteriaSetup: {
      active: rawFlags?.PAGE_ELIGIBILITY_CRITERIA_SETUP_ENABLED || false,
      skippable: true,
    },
    managerAssessment: {
      active: rawFlags?.PAGE_MANAGER_ASSESSMENT_ENABLED || false,
      skippable: true,
    },
    managerFeedback: {
      active: rawFlags?.PAGE_MANAGER_FEEDBACK_ENABLED || false,
      skippable: true,
    },
    roleSetting: {
      active: rawFlags?.PAGE_ROLE_SETTING_ENABLED || false,
      skippable: true,
    },
    translations: {
      active: rawFlags?.PAGE_TRANSLATIONS_ENABLED || false,
      skippable: true,
    },
    meetingsList: {
      active: rawFlags?.PAGE_MEETINGS_LIST_ENABLED || false,
      skippable: true,
    },
    meetingCreate: {
      active: rawFlags?.PAGE_MEETING_CREATE_ENABLED || false,
      skippable: true,
    },
    meetingDetails: {
      active: rawFlags?.PAGE_MEETING_DETAILS_ENABLED || false,
      skippable: true,
    },
    meetingShapes: {
      active: rawFlags?.PAGE_MEETING_SHAPES_ENABLED || false,
      skippable: true,
    },
    meetingEdit: {
      active: rawFlags?.PAGE_MEETING_EDIT_ENABLED || false,
      skippable: true,
    },
    meetingOverview: {
      active: rawFlags?.PAGE_MEETING_OVERVIEW_ENABLED || false,
      skippable: true,
    },
    meetingOverviewTalentPool: {
      active: rawFlags?.PAGE_MEETING_OVERVIEW_TALENT_POOL_ENABLED || false,
      skippable: true,
    },
    proxy: {
      active: rawFlags?.PAGE_PROXY_ENABLED || false,
      skippable: true,
    },
    talentCardPersonal: {
      active: rawFlags?.PAGE_TALENT_CARD_PERSONAL_ENABLED || false,
      skippable: true,
    },
    talentCardManager: {
      active: rawFlags?.PAGE_TALENT_CARD_MANAGER_ENABLED || false,
      skippable: true,
    },
    talentSearch: {
      active:
        rawFlags?.PAGE_TALENT_SEARCH_ENABLED ||
        rawFlags?.PAGE_TALENT_SEARCH_ENABLED_OVERRIDE ||
        false,
      skippable: true,
    },
    reportTalentSearch: {
      active: rawFlags?.PAGE_REPORT_TALENT_SEARCH_ENABLED || false,
      skippable: true,
    },
    reportMyOrganization: {
      active: rawFlags?.PAGE_REPORT_MY_ORGANIZATION_ENABLED || false,
      skippable: true,
    },
    reportTalentPerformance: {
      active: rawFlags?.PAGE_REPORT_TALENT_PERFORMANCE_ENABLED || false,
      skippable: true,
    },
    reportTalentPool: {
      active: rawFlags?.PAGE_REPORT_TALENT_POOL_ENABLED || false,
      skippable: true,
    },
    reportOpr: {
      active: rawFlags?.PAGE_REPORT_OPR_ENABLED || false,
      skippable: false,
    },
    reportMeetingDetails: {
      active: rawFlags?.PAGE_REPORT_MEETING_DETAILS_ENABLED || false,
      skippable: true,
    },
    documents: {
      active: rawFlags?.PAGE_DOCUMENTS_ENABLED || false,
      skippable: true,
    },
    feedbackConfiguration: {
      active: rawFlags?.FEEDBACK_CONFIGURATION_ENABLED || false,
      skippable: true,
    },
    funcManagerAssessmentFreeze: {
      active: rawFlags?.FUNC_MANAGER_ASSESSMENT_PROCESS_FREEZE || false,
      skippable: true,
    },
    dashboardPersonalV2: {
      active: rawFlags?.PAGE_DASHBOARD_V2_PERSONAL_ENABLED || false,
      skippable: false,
    },
    succession: {
      active: rawFlags?.PAGE_SUCCESSION || false,
      skippable: false,
    },
    talentPoolManagement: {
      active: rawFlags?.PAGE_TALENT_POOL_MANAGEMENT || false,
      skippable: false,
    },
    featureFlags: {
      active: true,
      skippable: false,
    },
  };

  return flags;
}
export async function getFeatureFlags(config: AxiosRequestConfig = {}) {
  const response = await oprApi.get<GetFeatureFlagsAPIResponse>(
    'feature-flag-management/access-control',
    config
  );

  return response.data;
}

export function useGetFeatureFlags() {
  return useInfiniteQuery(
    [FEATURE_FLAGS_QUERY_KEY],
    async ({ pageParam = 0 }) => {
      const response = await getFeatureFlags({});
      return {
        data: response,
        pageParam,
      };
    }
  );
}

export const GLOBAL_FEATURE_FLAGS_QUERY_KEY = 'useGetGlobalFeatureFlags';

export async function getGlobalFeatureFlags(config: AxiosRequestConfig = {}) {
  const response = await oprApi.get<GetFeatureFlagsAPIResponse>(
    'feature-flag-management',
    config
  );

  return response.data;
}

export function useGetGlobalFeatureFlags() {
  return useInfiniteQuery(
    [GLOBAL_FEATURE_FLAGS_QUERY_KEY],
    async ({ pageParam = 0 }) => {
      const response = await getGlobalFeatureFlags({});
      return {
        data: response,
        pageParam,
      };
    }
  );
}
